import { ColorFresnelModel } from './color-fresnel.model';
import { ScalarFresnelModel } from './scalar-fresnel.model';
import { ColorFresnelValue, ScalarFresnelValue, EngineColorValue } from './recipe-types';
import { EngineColorRecipeModel } from './engine-color-recipe.model';

function getScalarFresnelVal(
  recipe: EngineColorRecipeModel,
  prop: 'saturation' | 'brightness' | 'opacity' | 'contrast',
): ScalarFresnelValue | false | undefined {
  switch (true) {
    case recipe[prop] === undefined:
      return undefined;
    case recipe[prop] === false:
      return false;
    case typeof recipe[prop] === 'number':
      return (recipe[prop] as number) * 100;
    default:
      return new ScalarFresnelModel({
        center: recipe[prop][0],
        edge: recipe[prop][1],
        centerBias: recipe[prop][2],
        edgeBias: recipe[prop][3],
        power: recipe[prop][4],
      });
  }
}

// application color recipe
export class ColorRecipeModel {
  // false = remove color mode, undefined = don't touch color mod
  tintColor?: ColorFresnelValue | false | undefined;
  saturation?: ScalarFresnelValue | false | undefined;
  contrast?: ScalarFresnelValue | false | undefined;
  brightness?: ScalarFresnelValue | false | undefined;
  opacity?: ScalarFresnelValue | false | undefined;
  label?: string;

  constructor(value: ColorRecipeModel = {}) {
    const { tintColor, saturation, contrast, brightness, opacity, label } = value;
    this.tintColor = tintColor;
    this.saturation = saturation;
    this.contrast = contrast;
    this.brightness = brightness;
    this.opacity = opacity;
    this.label = label;
  }

  static isRealRecipe(recipe: ColorRecipeModel) {
    return (
      recipe.brightness !== false ||
      recipe.contrast !== false ||
      recipe.opacity !== false ||
      recipe.saturation !== false ||
      recipe.tintColor !== false
    );
  }

  static isFresnel(value: any): boolean {
    if (typeof value === 'undefined') {
      return false;
    }

    return (
      value.hasOwnProperty('edge') &&
      value.hasOwnProperty('edgeBias') &&
      value.hasOwnProperty('center') &&
      value.hasOwnProperty('centerBias') &&
      value.hasOwnProperty('power')
    );
  }

  static isColor(value: any): boolean {
    return Array.isArray(value) && value.length === 3;
  }

  static fromEngine(recipe: EngineColorRecipeModel): ColorRecipeModel {
    const tintColor = (() => {
      switch (true) {
        case recipe.tintColor === undefined:
          return undefined;
        case recipe.tintColor === false:
          return false;
        case Array.isArray(recipe.tintColor): {
          // at this point, we know tintColor is an array, so it's safe to cast.
          return (recipe.tintColor as number[]).length === 3
            ? [...(recipe.tintColor as number[])]
            : new ColorFresnelModel({
                center: recipe.tintColor[0],
                edge: recipe.tintColor[1],
                centerBias: recipe.tintColor[2],
                edgeBias: recipe.tintColor[3],
                power: recipe.tintColor[4],
              });
        }
      }
    })();
    return new ColorRecipeModel({
      tintColor: tintColor as ColorFresnelValue | false | undefined,
      saturation: getScalarFresnelVal(recipe, 'saturation'),
      brightness: getScalarFresnelVal(recipe, 'brightness'),
      contrast: getScalarFresnelVal(recipe, 'contrast'),
      opacity: getScalarFresnelVal(recipe, 'opacity'),
    });
  }

  /**
   *  Converts an Application Recipe to an Engine Recipe
   */
  static toEngine(recipe: ColorRecipeModel): EngineColorRecipeModel {
    // initialize values: false -> explicitly remove color setting
    let tintColor: EngineColorValue[] | false = recipe.tintColor === false ? false : undefined;
    let saturation: EngineColorValue | false = recipe.saturation === false ? false : undefined;
    let brightness: EngineColorValue | false = recipe.brightness === false ? false : undefined;
    let contrast: EngineColorValue | false = recipe.contrast === false ? false : undefined;
    let opacity: EngineColorValue | false = recipe.opacity === false ? false : undefined;

    // tint-color
    if (Array.isArray(recipe.tintColor)) {
      tintColor = [...recipe.tintColor];
    } else if (ColorRecipeModel.isFresnel(recipe.tintColor)) {
      tintColor = ColorFresnelModel.toEngine(recipe.tintColor as ColorFresnelModel);
    }

    // saturation
    if (typeof recipe.saturation === 'number') {
      saturation = recipe.saturation / 100.0;
    } else if (ColorRecipeModel.isFresnel(recipe.saturation)) {
      saturation = ScalarFresnelModel.toEngine(recipe.saturation as ScalarFresnelModel);
    }

    // brightness
    if (typeof recipe.brightness === 'number') {
      brightness = recipe.brightness / 100.0;
    } else if (ColorRecipeModel.isFresnel(recipe.brightness)) {
      brightness = ScalarFresnelModel.toEngine(recipe.brightness as ScalarFresnelModel);
    }

    // contrast
    if (typeof recipe.contrast === 'number') {
      contrast = recipe.contrast / 100.0;
    } else if (ColorRecipeModel.isFresnel(recipe.contrast)) {
      contrast = ScalarFresnelModel.toEngine(recipe.contrast as ScalarFresnelModel);
    }

    // opacity
    if (typeof recipe.opacity === 'number') {
      opacity = recipe.opacity / 100.0;
    } else if (ColorRecipeModel.isFresnel(recipe.opacity)) {
      opacity = ScalarFresnelModel.toEngine(recipe.opacity as ScalarFresnelModel);
    }

    return {
      tintColor,
      saturation,
      brightness,
      contrast,
      opacity,
    };
  }

  static mergeRecipes(
    currentRecipe: ColorRecipeModel,
    actionRecipe: ColorRecipeModel,
  ): ColorRecipeModel {
    const newRecipe = Object.assign({}, currentRecipe);

    // Do not overwrite current recipe value if new value is undefined
    for (const [key, value] of Object.entries(actionRecipe)) {
      if (typeof value !== 'undefined') {
        newRecipe[key] = value;
      }
    }

    return newRecipe;
  }

  static modifiesSaturation(recipe: ColorRecipeModel): boolean {
    return (
      (typeof recipe.saturation === 'number' && recipe.saturation !== 0) ||
      ColorRecipeModel.isFresnel(recipe.saturation)
    );
  }

  static modifiesContrast(recipe: ColorRecipeModel): boolean {
    return (
      (typeof recipe.contrast === 'number' && recipe.contrast !== 0) ||
      ColorRecipeModel.isFresnel(recipe.contrast)
    );
  }

  static modifiesBrightness(recipe: ColorRecipeModel): boolean {
    return (
      (typeof recipe.brightness === 'number' && recipe.brightness !== 0) ||
      ColorRecipeModel.isFresnel(recipe.brightness)
    );
  }

  static modifiesOpacity(recipe: ColorRecipeModel): boolean {
    return typeof recipe.opacity === 'number' || ColorRecipeModel.isFresnel(recipe.opacity);
  }
}
